import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../utils/api.js";
import { parseUTC } from "../../utils/date.js";
import { useAuthStore } from "../../store/auth.js";

interface SubmissionField {
  name: string;
  label: string;
  type: "text" | "textarea" | "url";
  required: boolean;
  placeholder?: string;
}

interface AssignmentInfo {
  candidateId: number;
  jobSlug: string;
  jobTitle: string;
  assignmentBriefHtml: string | null;
  pdfDownloadUrl: string | null;
  submissionFields: SubmissionField[];
  deadline: string | null;
  alreadySubmitted: boolean;
}

export function AssignmentSubmission() {
  const { candidateId } = useParams<{ slug: string; candidateId: string }>();
  const logout = useAuthStore((s) => s.logout);
  const [info, setInfo] = useState<AssignmentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api
      .get<AssignmentInfo>(`/assignments/${candidateId}`)
      .then((data) => {
        setInfo(data);
        if (data.alreadySubmitted) setSubmitted(true);
        const initial: Record<string, string> = {};
        for (const f of data.submissionFields) initial[f.name] = "";
        setValues(initial);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load assignment"))
      .finally(() => setLoading(false));
  }, [candidateId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!info) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/assignments/${candidateId}/submit`, values);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400 animate-pulse">Loading assignment...</div>;
  }

  if (error && !info) {
    return (
      <div className="max-w-2xl space-y-4">
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!info) return null;

  if (submitted) {
    return (
      <div className="max-w-2xl space-y-6">
        <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
          <div className="text-4xl mb-3">&#10003;</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Assignment Submitted</h1>
          <p className="text-gray-600">
            Thank you for completing the assignment for <strong>{info.jobTitle}</strong>. Our team
            will review your submission within <strong>5 business days</strong>.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            We&rsquo;ve sent a confirmation email to your inbox.
          </p>
        </div>
        <p className="text-xs text-gray-400">
          You can safely close this page.{" "}
          <button onClick={logout} className="text-gray-500 underline hover:text-gray-700">
            Sign out
          </button>
        </p>
      </div>
    );
  }

  const deadlineDate = info.deadline
    ? parseUTC(info.deadline).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{info.jobTitle}</h1>
        <p className="text-sm text-gray-500 mt-1">Take-home Assignment</p>
      </div>

      {info.pdfDownloadUrl && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Assignment Brief
          </h2>
          <a
            href={info.pdfDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
          >
            View Assignment PDF
          </a>
        </div>
      )}

      {!info.pdfDownloadUrl && info.assignmentBriefHtml && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Assignment Brief
          </h2>
          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: info.assignmentBriefHtml }}
          />
        </div>
      )}

      {deadlineDate && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span className="font-medium">Deadline:</span> {deadlineDate}
        </div>
      )}

      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <form
        onSubmit={handleSubmit}
        className="space-y-5 rounded-lg border border-gray-200 bg-white p-6"
      >
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          Your Submission
        </h2>

        {info.submissionFields.length === 0 ? (
          <p className="text-sm text-gray-500">
            No specific submission fields are configured. Please contact the hiring team for
            submission instructions.
          </p>
        ) : (
          info.submissionFields.map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {field.label}
                {field.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  value={values[field.name] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  placeholder={field.placeholder}
                  required={field.required}
                  rows={4}
                  className="input"
                />
              ) : (
                <input
                  type={field.type}
                  value={values[field.name] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="input"
                />
              )}
            </div>
          ))
        )}

        {info.submissionFields.length > 0 && (
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] disabled:opacity-50 transition-colors"
          >
            {submitting ? "Submitting..." : "Submit Assignment"}
          </button>
        )}
      </form>
    </div>
  );
}
